import { css } from 'aphrodite';
import * as React from 'react';
import { IRichContentEditorState } from '../../../viewmodels/AppViewModels';
import { RichContentDocumentEditor } from '../../../web/components/richContent/RichContentDocumentEditor';
import { Button } from '../../../web/components-v2/Button';
import { Input } from '../../../web/components-v2/Input';
import { Select, SelectContent, SelectItem, SelectTrigger } from '../../../web/components-v2/Select';
import { titles } from '../../../web/styles/colors';
import { bs } from '../../../web/styles/styles';
import {
	buildStartMonthOptions,
	ContentPlanOutputMix,
	MONTH_COUNT_OPTIONS,
	OUTPUT_MIX_OPTIONS,
} from './contentPlanOptions';
import { styleSheet } from './styles';

const ContentTeamEditorCSS = `
	body {
		background: transparent;
		color: ${titles};
		font-family: "Sora", sans-serif;
		font-size: 14px;
		margin: 0;
		padding: 16px 20px;
	}
`;

export interface IContentTeamTabProps {
	websiteUrl: string;
	onWebsiteUrlChange: (value: string) => void;
	outputMix: ContentPlanOutputMix | null;
	onOutputMixChange: (value: ContentPlanOutputMix) => void;
	/** First month to plan, as "yyyy-MM". */
	startMonth: string;
	onStartMonthChange: (value: string) => void;
	monthCount: number;
	onMonthCountChange: (value: number) => void;
	newsletterTemplateId: string;
	onNewsletterTemplateIdChange: (value: string) => void;
	hasGeneratedSummary: boolean;
	contentState: IRichContentEditorState | null;
	onContentStateChange: (state: IRichContentEditorState | null) => void;
	isGenerating: boolean;
	isSubmitting: boolean;
	onGenerate: () => void;
	onSkip: () => void;
	onSubmit: () => void;
}

/** Post-meeting "Content Team" tab, shared by the Log Meeting Notes page and the Find Recordings flow. */
export const ContentTeamTab: React.FC<IContentTeamTabProps> = ({
	websiteUrl,
	onWebsiteUrlChange,
	outputMix,
	onOutputMixChange,
	startMonth,
	onStartMonthChange,
	monthCount,
	onMonthCountChange,
	newsletterTemplateId,
	onNewsletterTemplateIdChange,
	hasGeneratedSummary,
	contentState,
	onContentStateChange,
	isGenerating,
	isSubmitting,
	onGenerate,
	onSkip,
	onSubmit,
}) => {
	// Mount-only: the options are relative to today, which does not move within a session.
	const startMonthOptions = React.useMemo(() => buildStartMonthOptions(), []);

	if (hasGeneratedSummary) {
		return (
			<div className={css(styleSheet.confirmationColumn, styleSheet.confirmationColumnFull)}>
				<div className={css(styleSheet.summaryHeader)}>
					<h3>Content Team Intake Summary</h3>
				</div>
				<div className={css(styleSheet.summaryNote)}>
					Edit the content below, then click Submit to save to the account and send to ClickUp.
				</div>
				<div className={css(styleSheet.contentTeamEditorContainer)}>
					<RichContentDocumentEditor
						contentState={contentState}
						onContentStateChanged={onContentStateChange}
						readOnly={isSubmitting}
						config={{
							autoresizeToFitContent: true,
							minHeight: 400,
							contentHorizontalPadding: 20,
							toolbar: 'undo redo | bold italic underline | bullist numlist',
							contentRawCss: ContentTeamEditorCSS,
						}}
					/>
				</div>
				<div className={css(styleSheet.formActions)}>
					<Button
						variant='primary'
						onClick={onSubmit}
						isLoading={isSubmitting}
						disabled={isSubmitting || !contentState?.hasContent()}
					>
						Submit to ClickUp
					</Button>
				</div>
			</div>
		);
	}

	return (
		<div className={css(styleSheet.confirmationColumn, styleSheet.confirmationColumnFull)}>
			<div className={css(styleSheet.confirmationPanel)}>
				<h3 className={css(styleSheet.confirmationPanelTitle)}>Content Team Intake</h3>
				<div className={css(styleSheet.deepResearchFields)}>
					<div>
						<label className={css(styleSheet.deepResearchFieldLabel)}>Website URL</label>
						<Input
							type='text'
							value={websiteUrl}
							onChange={e => onWebsiteUrlChange(e.target.value)}
							placeholder='e.g., https://example.com'
							disabled={isGenerating}
						/>
					</div>
					<div>
						<label className={css(styleSheet.deepResearchFieldLabel)}>Output</label>
						<Select value={outputMix ?? ''} onValueChange={value => onOutputMixChange(value as ContentPlanOutputMix)}>
							<SelectTrigger disabled={isGenerating}>
								{outputMix ? (
									OUTPUT_MIX_OPTIONS.find(option => option.value === outputMix)?.label
								) : (
									<span className={css(bs.textDarkGrayFontColor)}>Select the content mix</span>
								)}
							</SelectTrigger>
							<SelectContent>
								{OUTPUT_MIX_OPTIONS.map(option => (
									<SelectItem key={option.value} value={option.value}>
										{option.label}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					</div>
					<div className={css(styleSheet.contentPlanMonthRow)}>
						<div className={css(styleSheet.contentPlanMonthField)}>
							<label className={css(styleSheet.deepResearchFieldLabel)}>Starting month</label>
							<Select value={startMonth} onValueChange={onStartMonthChange}>
								<SelectTrigger disabled={isGenerating}>
									{startMonthOptions.find(option => option.value === startMonth)?.label}
								</SelectTrigger>
								<SelectContent>
									{startMonthOptions.map(option => (
										<SelectItem key={option.value} value={option.value}>
											{option.label}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</div>
						<div className={css(styleSheet.contentPlanMonthField)}>
							<label className={css(styleSheet.deepResearchFieldLabel)}>How many months</label>
							<Select value={String(monthCount)} onValueChange={value => onMonthCountChange(Number(value))}>
								<SelectTrigger disabled={isGenerating}>
									{MONTH_COUNT_OPTIONS.find(option => option.value === String(monthCount))?.label}
								</SelectTrigger>
								<SelectContent>
									{MONTH_COUNT_OPTIONS.map(option => (
										<SelectItem key={option.value} value={option.value}>
											{option.label}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</div>
					</div>
					<div>
						<label className={css(styleSheet.deepResearchFieldLabel)}>Newsletter template ID (optional)</label>
						<Input
							type='text'
							value={newsletterTemplateId}
							onChange={e => onNewsletterTemplateIdChange(e.target.value)}
							placeholder='The template the client picked for the content team to brand'
							disabled={isGenerating}
						/>
					</div>
				</div>
				<div className={css(styleSheet.confirmationActions)}>
					<Button variant='secondary' onClick={onSkip} disabled={isGenerating}>
						Skip
					</Button>
					<Button
						variant='success'
						onClick={onGenerate}
						isLoading={isGenerating}
						disabled={isGenerating || !websiteUrl || !outputMix}
					>
						Generate Content Team Summary
					</Button>
				</div>
			</div>
		</div>
	);
};
